import { reorderMockExamsApi } from "@/api/services/admin/learning-content";
import { TMockExamListItemDto } from "@/api/services/admin/learning-content/dtos/get-mock-exam.dto";
import { CardContainer } from "@/components/commons/CardContainer";
import { Button } from "@/components/elements/Button";
import { IconPlus } from "@/assets/icons";
import { API_SUCCESS_MESSAGE } from "@/constants/common";
import { QUERY_KEYS } from "@/constants/queryKeys";
import { MockExamsTable } from "@/features/learning-content/components/MockExamsTable";
import { useMockExamList } from "@/features/learning-content/hooks/useMockExamList";
import { useToast } from "@/hooks/useToast";
import { AppRoutes } from "@/routes/config";
import { useDraggableTable } from "@/hooks/useDraggableTable";
import { showError } from "@/utils/error";
import { getDynamicRoute } from "@/utils/url";
import { rounded } from "@/theme/variables";
import { Stack, Typography } from "@mui/material";
import { useQueryClient } from "@tanstack/react-query";
import { FC, useMemo } from "react";
import { useNavigate } from "react-router-dom";
import { EMPTY_STR } from "@/constants/string";

type TMockExamContentSectionProps = {
  industryId: string;
};

// 模擬試験コンテンツ section for the industry detail page: list + reorder + 有効 toggle + summary
export const MockExamContentSection: FC<TMockExamContentSectionProps> = ({ industryId }) => {
  const navigate = useNavigate();
  const toast = useToast();
  const queryClient = useQueryClient();

  const { data: mockExamList, isLoading } = useMockExamList(industryId);

  // Drag-and-drop reordering for the mock exam list
  const { rows, isSaving, onDragEnd } = useDraggableTable<TMockExamListItemDto>({
    initialData: mockExamList,
    getItemId: (item) => item.id,
    onReorder: async (orderedIds) => {
      if (!industryId) return;
      try {
        await reorderMockExamsApi(industryId, { mockExamIds: orderedIds as number[] });
        toast.success(API_SUCCESS_MESSAGE);
        queryClient.invalidateQueries({ queryKey: [QUERY_KEYS.LEARNING_CONTENT.FETCH_MOCK_EXAM_LIST, industryId] });
      } catch (error) {
        showError(error);
        throw error; // Re-throw to trigger rollback in hook
      }
    },
  });

  // Summary shown below the list: counts across ACTIVE (有効) exams only
  const summary = useMemo(() => {
    let activeCount = 0;
    let questionTotal = 0;
    rows.forEach((exam) => {
      if (exam.isActive) {
        activeCount += 1;
        questionTotal += exam.totalQuestionCount || 0;
      }
    });
    return { activeCount, questionTotal };
  }, [rows]);

  const handleAddMockExam = () => {
    if (industryId) {
      navigate(getDynamicRoute(AppRoutes.learningContentMockExamCreate, { id: industryId }));
    }
  };

  return (
    <CardContainer>
      <Stack gap={3}>
        <Stack direction="row" justifyContent="space-between" alignItems="center">
          <Typography variant="sub16Bold">模擬試験コンテンツ</Typography>
          <Button size="sm" variant="outline" onClick={handleAddMockExam} startIcon={<IconPlus />}>
            新しい模擬試験を登録
          </Button>
        </Stack>
        <Typography variant="body14" color="text.secondary">
          模擬試験データはカリキュラムを全て終えた後に実施してもらう想定の実試験を模した問題データです。
        </Typography>
        <MockExamsTable isLoading={isLoading} isFetching={isSaving} data={rows} industryId={industryId} onDragEnd={onDragEnd} />
        <Stack direction="row" alignItems="center" gap={4} border="1px solid" borderColor="divider" borderRadius={rounded.sm} px={3} py={2}>
          <Typography variant="body14">有効な模擬試験の数：{summary.activeCount || EMPTY_STR.TEXT}件</Typography>
          <Typography variant="body14">
            累計登録問題数：{summary.questionTotal ? summary.questionTotal.toLocaleString() : EMPTY_STR.TEXT}件
          </Typography>
        </Stack>
      </Stack>
    </CardContainer>
  );
};
